import * as React from 'react'
import { usePageNotifications, useAuth, useSystemApi } from '../../hooks'
import {
  BlueGreenConfigurationViewModel,
  SupportUserPermission,
} from '../../api'
import {
  makeStyles,
  Theme,
  createStyles,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { AsyncPayload } from '../base'
import { Box, Loader, Button } from 'cs-kevlar-react'
import { KeyValueText } from 'components/KeyValueText'

interface EditBlueGreenConfigurationProps {
  onClose?: () => void
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    root: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(8),
    },
    item: {
      '& + &': {
        marginTop: theme.spacing(8),
      },
    },
    action: {
      marginTop: theme.spacing(8),
    },
  }),
)

export const EditBlueGreenConfiguration: React.FunctionComponent<EditBlueGreenConfigurationProps> = ({
  onClose,
}) => {
  const classes = useStyles()
  const { user: authUser } = useAuth()
  const { dispatch: dispatchNotification } = usePageNotifications()

  const {
    getBlueGreenConfiguration,
    notifyBlueGreenStarted: invokeNotifyBlueGreenStarted,
    notifyBlueGreenEnded: invokeNotifyBlueGreenEnded,
    requestImportLockDrain: invokeRequestImportLockDrain,
    stopImportLockDrain: invokeStopImportLockDrain,
  } = useSystemApi()

  const [blueGreenConfig, setBlueGreenConfig] = React.useState<
    AsyncPayload<BlueGreenConfigurationViewModel>
  >({
    payload: null,
    isLoading: true,
  })

  if (authUser === null) return null

  const loadBlueGreenConfiguration = async () => {
    const response = await getBlueGreenConfiguration()
    if (response.status === 200 && response.data) {
      setBlueGreenConfig({
        payload: response.data,
        isLoading: false,
      })
    }
  }

  React.useEffect(() => {
    loadBlueGreenConfiguration()
  }, [])

  const notifyBlueGreenStarted = async () => {
    setBlueGreenConfig({
      payload: blueGreenConfig.payload,
      isLoading: true,
    })

    try {
      const response = await invokeNotifyBlueGreenStarted()
      await loadBlueGreenConfiguration()

      if (response.status === 200) {
        dispatchNotification({
          type: 'SetSuccess',
          payload: 'Blue/Green start notified',
        })
      }
    } catch (e) {
      dispatchNotification({
        type: 'SetError',
        payload: 'There was an error notifying the start of a blue/green',
      })
    }
  }

  const notifyBlueGreenEnded = async () => {
    setBlueGreenConfig({
      payload: blueGreenConfig.payload,
      isLoading: true,
    })
    try {
      const response = await invokeNotifyBlueGreenEnded()
      await loadBlueGreenConfiguration()

      if (response.status === 200) {
        dispatchNotification({
          type: 'SetSuccess',
          payload: 'Blue/Green end notified',
        })
      }
    } catch (e) {
      dispatchNotification({
        type: 'SetError',
        payload: 'There was an error updating the blue/green status',
      })
    }
  }

  const requestImportLockDrain = async () => {
    setBlueGreenConfig({
      payload: blueGreenConfig.payload,
      isLoading: true,
    })

    try {
      const response = await invokeRequestImportLockDrain()
      await loadBlueGreenConfiguration()

      if (response.status === 200) {
        dispatchNotification({
          type: 'SetSuccess',
          payload: 'Import lock drain started',
        })
      }
    } catch (e) {
      dispatchNotification({
        type: 'SetError',
        payload: 'There was an error requesting the import lock drain',
      })
    }
  }

  const stopImportLockDrain = async () => {
    setBlueGreenConfig({
      payload: blueGreenConfig.payload,
      isLoading: true,
    })

    try {
      const response = await invokeStopImportLockDrain()
      await loadBlueGreenConfiguration()

      if (response.status === 200) {
        dispatchNotification({
          type: 'SetSuccess',
          payload: 'Import lock drain stopped',
        })
      }
    } catch (e) {
      dispatchNotification({
        type: 'SetError',
        payload: 'There was an error stopping the import lock drain',
      })
    }
  }

  const canEdit =
    authUser.isSupport &&
    authUser.permissions.includes(
      SupportUserPermission.ManageBlueGreenConfiguration,
    )

  return (
    <React.Fragment>
      {blueGreenConfig.isLoading && (
        <Loader size="sm" text="Loading Blue Green Configuration" />
      )}
      {blueGreenConfig.payload !== null && (
        <Box className={classes.root}>
          <KeyValueText className={classes.item} label="Is Blue/Green Running">
            {blueGreenConfig.payload.isBlueGreenRunning.toString()}
          </KeyValueText>
          <KeyValueText
            className={classes.item}
            label="Is Import Lock Draining Active"
          >
            {blueGreenConfig.payload.isImportLockDrainingActive.toString()}
          </KeyValueText>
          <Box className={classes.spacer} />
          {canEdit && (
            <React.Fragment>
              <Button
                className={classes.action}
                onClick={notifyBlueGreenStarted}
              >
                Notify Blue/Green Started
              </Button>
              <Button className={classes.action} onClick={notifyBlueGreenEnded}>
                Notify Blue/Green Ended
              </Button>
              <Button
                className={classes.action}
                onClick={requestImportLockDrain}
                color="secondary"
              >
                Request Import Lock Draining
              </Button>
              <Button className={classes.action} onClick={stopImportLockDrain}>
                Stop Import Lock Draining
              </Button>
            </React.Fragment>
          )}
        </Box>
      )}
    </React.Fragment>
  )
}
